import React, { useState, useEffect } from 'react'
import Input from '../input'
import Select from '../select'
import RoleBasedAccess from '../role-based-access'
import useI18n from '../../i18n/useI18n'

/**
 * Genel amaçlı filtre kartı bileşeni
 * @param {Object} props
 * @param {Object} props.filters - Filtre değerleri
 * @param {Function} props.onFilterChange - Filtre değiştiğinde çağrılır
 * @param {Function} props.onFilterSubmit - Form submit edildiğinde çağrılır
 * @param {Function} props.onClearFilters - Filtreler temizlendiğinde çağrılır
 * @param {Function} props.onRefresh - Tabloyu yenileme fonksiyonu
 * @param {boolean} props.loading - Yükleme durumu
 * @param {string} props.title - Filtre başlığı (i18n key)
 * @param {string} props.titleHide - Gizle butonu tooltip (i18n key)
 * @param {string} props.titleShow - Göster butonu tooltip (i18n key)
 * @param {Array} props.fields - Filtre alanları konfigürasyonu
 * @param {Object} props.defaultFilters - Varsayılan filtre değerleri (temizleme için)
 * @param {Array} props.autoSubmitFields - Otomatik submit yapılacak alan isimleri
 * @param {boolean} props.defaultOpen - İlk açılışta açık mı (default: true)
 */
const FilterCard = ({
  filters,
  onFilterChange,
  onFilterSubmit,
  onClearFilters,
  onRefresh,
  loading = false,
  title,
  titleHide,
  titleShow,
  fields = [],
  defaultFilters = {},
  autoSubmitFields = [],
  defaultOpen = true,
  topContent = null
}) => {
  const [localFilters, setLocalFilters] = useState(filters)
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const { t } = useI18n()

  const translatablePrefixes = [
    'COMMON_',
    'UNIVERSITIES_',
    'PROGRAMS_',
    'STUDENTS_',
    'APPLICATIONS_',
    'AGENTS_',
    'ANNOUNCEMENTS_'
  ]

  const translateIfNeeded = (value) => {
    if (
      typeof value === 'string' &&
      translatablePrefixes.some((prefix) => value.startsWith(prefix))
    ) {
      return t(value)
    }
    return value
  }

  const handleInputChange = (name, value) => {
    const newFilters = {
      ...localFilters,
      [name]: value
    }
    setLocalFilters(newFilters)
    onFilterChange(newFilters)
  }

  const handleSelectChange = (name, value) => {
    const newFilters = {
      ...localFilters,
      [name]: value
    }
    setLocalFilters(newFilters)
    onFilterChange(newFilters)
    // Auto-submit for specified fields
    if (autoSubmitFields.includes(name)) {
      onFilterSubmit(newFilters)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onFilterSubmit(localFilters)
  }

  const handleClear = () => {
    setLocalFilters(defaultFilters)
    onClearFilters(defaultFilters)
  }

  // Sync local state with props when filters change externally
  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const toggleFilter = () => {
    setIsOpen(!isOpen)
  }

  const renderField = (field, index) => {
    const {
      type,
      name,
      colSize = 'col-lg-3 col-md-6 col-sm-12',
      placeholder,
      icon,
      options = [],
      roleBased = false,
      roleAccess,
      ...fieldProps
    } = field

    const fieldValue = localFilters[name] || ''
    const fieldElement = (
      <div className={colSize} key={index}>
        <div className="form-group mb-4">
          {type === 'input' && (
            <Input
              type={fieldProps.inputType || fieldProps.type || "text"}
              name={name}
              value={fieldValue}
              onChange={(e) => handleInputChange(name, e.target.value)}
              placeholder={translateIfNeeded(placeholder)}
              icon={icon}
              {...fieldProps}
            />
          )}
          {type === 'select' && (
            <Select
              name={name}
              value={fieldValue}
              onChange={(e) => handleSelectChange(name, e.target.value)}
              placeholder={translateIfNeeded(placeholder)}
              options={options.map(opt => ({
                ...opt,
                label: translateIfNeeded(opt.label)
              }))}
              icon={icon}
              {...fieldProps}
            />
          )}
        </div>
      </div>
    )

    if (roleBased && roleAccess) {
      return (
        <RoleBasedAccess allowedRoles={roleAccess} key={index}>
          {fieldElement}
        </RoleBasedAccess>
      )
    }

    return fieldElement
  }

  return (
    <div className="card">
      <div onClick={toggleFilter} className="header">
        <h2>
          <strong>{t(title)}</strong>
        </h2>
        <ul className="header-dropdown">
          <li>
            <button
              type="button"
              className="btn btn-link"
              onClick={toggleFilter}
              aria-expanded={isOpen}
              title={isOpen ? t(titleHide) : t(titleShow)}
            >
              <i className={`zmdi zmdi-chevron-${isOpen ? 'up' : 'down'}`}></i>
            </button>
          </li>
        </ul>
      </div>
      <div className={`body ${isOpen ? '' : 'collapse'}`} style={{ display: isOpen ? 'block' : 'none' }}>
        {topContent && <div className="filter-card-top m-b-20">{topContent}</div>}
        <form onSubmit={handleSubmit}>
          <div className="row clearfix">
            {fields.map((field, index) => renderField(field, index))}
            <div className="col-lg-12 col-md-12 col-sm-12">
              <div className="form-group mb-0" style={{ textAlign: 'center' }}>
                <button
                  type="submit"
                  className="btn btn-raised btn-round btn-primary m-r-10"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <i className="zmdi zmdi-search"></i>
                  <span>{t('COMMON_ACTIONS_SEARCH')}</span>
                </button>
                <button
                  type="button"
                  className="btn btn-raised btn-round btn-error m-r-10"
                  onClick={handleClear}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <i className="zmdi zmdi-close-circle"></i>
                  <span>{t('COMMON_ACTIONS_CLEAR_FILTERS')}</span>
                </button>
                {/* {onRefresh && (
                  <button
                    type="button"
                    className="btn btn-raised btn-round btn-info"
                    onClick={onRefresh}
                    disabled={loading}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    title={t('COMMON_ACTIONS_REFRESH')}
                  >
                    <i className={`zmdi zmdi-refresh ${loading ? 'zmdi-hc-spin' : ''}`}></i>
                    <span>{t('COMMON_ACTIONS_REFRESH')}</span>
                  </button>
                )} */}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FilterCard

